import React from 'react';
import {
    VStack,
    Box,
    Text,
    HStack,
    Badge,
} from '@chakra-ui/react';

/**
 * Transfer Proof Template Component for displaying JSON transfer proof data
 * @param {object} proofData - The parsed JSON proof data
 * @returns {JSX.Element} - Formatted transfer proof display
 */
const TransferProofTemplate = ({ proofData }) => {
    const { finnetResponse, transferDetails, timestamp } = proofData;

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString('id-ID', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short'
        });
    };

    const getStatusColor = (responseCode) => {
        return responseCode === '2000000' ? 'green' : 'red';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    return (
        <VStack spacing={6} align="stretch">
            {/* Header */}
            <Box
                bg="blue.50"
                p={4}
                borderRadius="md"
                border="1px"
                borderColor="blue.200"
                textAlign="center"
            >
                <Text fontSize="lg" fontWeight="bold" color="blue.700" mb={2}>
                    🧾 BUKTI TRANSFER ELEKTRONIK
                </Text>
                <Text fontSize="sm" color="blue.600">
                    Transaksi berhasil diproses melalui sistem Finnet
                </Text>
            </Box>

            {/* Transaction Status */}
            <Box
                bg={getStatusColor(finnetResponse.responseCode) === 'green' ? 'green.50' : 'red.50'}
                p={4}
                borderRadius="md"
                border="1px"
                borderColor={getStatusColor(finnetResponse.responseCode) === 'green' ? 'green.200' : 'red.200'}
            >
                <HStack justify="space-between" align="center">
                    <VStack align="start" spacing={1}>
                        <Text fontSize="sm" color="gray.600">Status Transaksi</Text>
                        <HStack>
                            <Badge
                                colorScheme={getStatusColor(finnetResponse.responseCode)}
                                fontSize="sm"
                                px={3}
                                py={1}
                            >
                                {finnetResponse.responseCode === '2000000' ? '✅ BERHASIL' : '❌ GAGAL'}
                            </Badge>
                        </HStack>
                    </VStack>
                    <VStack align="end" spacing={1}>
                        <Text fontSize="sm" color="gray.600">Kode Referensi</Text>
                        <Text fontSize="sm" fontWeight="mono" color="gray.800">
                            {finnetResponse.refCode}
                        </Text>
                    </VStack>
                </HStack>
                <Text fontSize="sm" color="gray.700" mt={2}>
                    {finnetResponse.responseMessage}
                </Text>
            </Box>

            {/* Transfer Details */}
            <Box bg="gray.50" p={4} borderRadius="md" border="1px" borderColor="gray.200">
                <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.700">
                    📋 Detail Transfer
                </Text>
                <VStack spacing={3} align="stretch">
                    <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Jumlah Transfer:</Text>
                        <Text fontSize="sm" fontWeight="bold" color="green.600">
                            {formatCurrency(transferDetails.amount)}
                        </Text>
                    </HStack>
                    <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Biaya Admin:</Text>
                        <Text fontSize="sm" fontWeight="medium" color="orange.600">
                            {formatCurrency(finnetResponse.feeAmount)}
                        </Text>
                    </HStack>
                    <Box height="1px" bg="gray.300" />
                    <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Rekening Asal:</Text>
                        <Text fontSize="sm" fontWeight="medium">{transferDetails.fromAccount}</Text>
                    </HStack>
                    <HStack justify="space-between" align="start">
                        <Text fontSize="sm" color="gray.600">Rekening Tujuan:</Text>
                        <VStack align="end" spacing={1}>
                            <Text fontSize="sm" fontWeight="medium">{transferDetails.toAccount}</Text>
                            <Text fontSize="xs" color="gray.500">{transferDetails.accountName}</Text>
                        </VStack>
                    </HStack>
                    <HStack justify="space-between" align="start">
                        <Text fontSize="sm" color="gray.600">Keterangan:</Text>
                        <Text fontSize="sm" textAlign="right" maxW="200px">
                            {transferDetails.description}
                        </Text>
                    </HStack>
                </VStack>
            </Box>

            {/* Transaction Info */}
            <Box bg="blue.50" p={4} borderRadius="md" border="1px" borderColor="blue.200">
                <Text fontSize="md" fontWeight="semibold" mb={3} color="blue.700">
                    ⏱️ Informasi Transaksi
                </Text>
                <VStack spacing={2} align="stretch">
                    <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Waktu Transaksi:</Text>
                        <Text fontSize="sm" fontWeight="medium">{formatTimestamp(timestamp)}</Text>
                    </HStack>
                    <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Nama Bank Tujuan:</Text>
                        <Text fontSize="sm" fontWeight="medium">{finnetResponse.destAccName}</Text>
                    </HStack>
                </VStack>
            </Box>

            {/* Footer */}
            <Box
                bg="gray.100"
                p={3}
                borderRadius="md"
                textAlign="center"
                fontSize="xs"
                color="gray.500"
            >
                <Text>
                    Bukti transfer ini dibuat secara otomatis oleh sistem dan sah sebagai bukti pembayaran.
                </Text>
                <Text mt={1}>
                    Untuk pertanyaan lebih lanjut, hubungi customer service dengan menyertakan kode referensi.
                </Text>
            </Box>
        </VStack>
    );
};

export default TransferProofTemplate;